import { isValid } from '@nostr/tools/nip05'
import { useEffect, useState } from 'react'
import { useFetchProfile } from '@/hooks/useFetchProfile'
import { HugeiconsIcon } from '@hugeicons/react'
import { BadgeAlertIcon, BadgeCheckIcon } from '@hugeicons/core-free-icons'
import { Favicon } from './Favicon'
import { NostrUser } from '@nostr/gadgets/metadata'

const nip05Cache = new Map<string, Promise<boolean>>()

export default function Nip05({ user, append }: { user: string | NostrUser; append?: string }) {
  const { profile } = useFetchProfile(user)
  const pubkey = profile?.pubkey

  const nip05 = typeof profile?.metadata?.nip05 === 'string' ? profile.metadata.nip05 : undefined
  const [nip05IsVerified, setNip05IsVerified] = useState<boolean | null>(null)

  useEffect(() => {
    if (!nip05 || !pubkey) {
      setNip05IsVerified(null)
      return
    }

    const [name, ...domainParts] = nip05.split('@')
    const domain = domainParts.join('@')
    if (!name || !domain) {
      setNip05IsVerified(null)
      return
    }

    const key = `${nip05}:${pubkey}`
    setNip05IsVerified(null)
    if (!nip05Cache.has(key)) {
      nip05Cache.set(key, isValid(pubkey, nip05 as `${string}@${string}`))
    }
    nip05Cache.get(key)!.then(setNip05IsVerified)
  }, [nip05, pubkey])

  const [nip05Name, ...nip05DomainParts] = nip05?.split('@') ?? []
  const nip05Domain = nip05DomainParts.join('@')

  if (!nip05 || !nip05Name || !nip05Domain) return null

  if (nip05IsVerified === null || !nip05IsVerified) return null

  return (
    <a
      href={`https://${nip05Domain}/.well-known/nostr.json?name=${nip05Name}`}
      target="_blank"
      className="flex items-center gap-1 [&_svg]:!size-3.5 [&_svg]:shrink-0"
    >
      {nip05Name !== '_' ? (
        <span className="text-sm text-muted-foreground">@{nip05Name}</span>
      ) : null}
      {nip05IsVerified ? (
        <Favicon
          domain={nip05Domain}
          className="w-3.5 h-3.5 rounded-full"
          fallback={<HugeiconsIcon icon={BadgeCheckIcon} className="text-primary" />}
        />
      ) : (
        <HugeiconsIcon icon={BadgeAlertIcon} className="text-muted-foreground" />
      )}
      <span className="truncate min-w-0">{nip05Domain}</span>
      {append && <span className="text-sm text-muted-foreground truncate">{append}</span>}
    </a>
  )
}
